import {Component, Input, ViewChild} from '@angular/core';
import {ChartBaseComponent} from '../base/chart-base/chart-base.component';
import {Subject, takeUntil} from 'rxjs';
import {Store} from '@ngrx/store';
import * as fromRoot from '../../../../../state/app.state';
import {LineChartDataModel} from '../../../../../models/line-chart-data.model';
import {LineChartComponent} from '../../../../shared/components/charts/line-chart/line-chart.component';
import {WeightChartTableData} from '../../models/fetch-weight-chart-table-data.model';
import {weightChartData, weightChartTableData} from '../../core/progress.selectors';
import {FetchWeightChartDataRequest, FetchWeightChartTableDataRequest} from '../../core/progress.actions';
import {CommonService} from '../../../../shared/services/common.service';
import {PrintService} from '../../../../shared/services/print.service';
import moment from 'moment';

@Component({
  selector: 'app-weight-chart',
  templateUrl: './weight-chart.component.html',
  styleUrls: ['./weight-chart.component.css']
})
export class WeightChartComponent extends ChartBaseComponent {
  // for print
  @Input() printData: any;

  @ViewChild(LineChartComponent, {static: false}) lineChartComponent: LineChartComponent;
  onDestroy = new Subject<void>();
  chartData: {
    loading: boolean;
    data: LineChartDataModel
  };
  chartTableData: {
    loading: boolean;
    data: WeightChartTableData
  };
  fromDate: string;
  toDate: string;
  legendList = [
    {
      label: 'Coach',
      color: 'rgb(224, 79, 57)',
      isCoachEntered: true,
      checked: true,
      lineId: 'line_coach',
    }
  ]
  roleDecorators = {
    1: {
      label: 'Coach',
      color: 'rgb(224, 79, 57)'
    },
    2: {
      label: 'Family',
      color: 'rgb(39, 153, 137)'
    },
  }
  columns = [
    'addedBy',
    'recordedOn',
    //'sessions',
    'value',
  ];

  isCoachEntered: boolean = null;
  initialLoad = true;
  additionalText = '';
  sortColumn = 'recordedOn';
  sortOrder: 'asc' | 'desc' = 'asc';

  constructor(
    private store: Store<fromRoot.State>,
    private commonService: CommonService,
    private printService: PrintService,
  ) {
    super();
  }

  override onDateChange(data: {
    fromDate: string;
    toDate: string
  }) {
    this.fromDate = data.fromDate;
    this.toDate = data.toDate;

    // fetch chart details
    this.fetchChartDetails();
  }

  override onPrint(complete: () => void) {
    if (!this.chartData?.data) {
      this.commonService.notification('Nothing to print.', 'danger');
      complete();
      return;
    }
    // this.commonService.printElement('weight-chart-container');
    this.printService.open({
      component: WeightChartComponent,
      data: {
        fromDate: this.fromDate,
        toDate: this.toDate,
        isCoachEntered: this.isCoachEntered,
        memberId: this.memberId,
        groupId: this.groupId,
        userName: this.getUserName(this.profile),
        title: this.getPageTitle({
          profile: this.profile,
          fromDate: this.fromDate,
          toDate: this.toDate,
          chartName: this.chartName,
        }),
      },
      onClose: complete,
    })
  }

  ngOnInit() {

    this.legendList.push(
      {
        label: this.isAdult ? 'Adult': 'Participant',
        color: 'rgb(39, 153, 137)',
        isCoachEntered: false,
        checked: true,
        lineId: 'line_participant',
      }
    );
    
    if (this.printData) {
      this.fromDate = this.printData['fromDate']
      this.toDate = this.printData['toDate']
      this.isCoachEntered = this.printData['isCoachEntered']
      this.memberId = this.printData['memberId']
      this.groupId = this.printData['groupId']

      // mark check boxes
      if (this.isCoachEntered !== null) {
        this.legendList.forEach((item) => {
          item.checked = item.isCoachEntered === this.isCoachEntered;
        })
      }

      // append date to title
      if (this.fromDate && this.toDate) {
        this.additionalText = `[${moment(this.fromDate).format('MMM DD, YYYY')} - ${moment(this.toDate).format('MMM DD, YYYY')}]`
      } else if (this.fromDate) {
        this.additionalText = `[From ${moment(this.fromDate).format('MMM DD, YYYY')}]`
      } else if (this.toDate) {
        this.additionalText = `[Till ${moment(this.toDate).format('MMM DD, YYYY')}]`
      }
    }
    this.store.select(weightChartData)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        if (data.forPrint && !this.printData) {
          // skip listening data for print from normal component
          return;
        }

        this.chartData = data;

        // check is it for print
        if (!this.chartData?.loading && this.printData) {
          this.checkDataReadyForPrint();
        }
      });

    this.store.select(weightChartTableData)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        if (data.forPrint && !this.printData) {
          // skip listening data for print from normal component
          return;
        }

        this.chartTableData = data;
        // check is it for print
        if (!data.loading && this.printData) {
          this.checkDataReadyForPrint();
        }

        if (!data.loading && data.data?.hasNext) {
          setTimeout(() => {
            // check window height for infinite scroll
            this.commonService.checkInfiniteScrollHeightAndCallNext('table-wrapper', () => {
              this.onScroll();
            });
          }, 0)
        }
      });

    if (this.printData) {
      this.fetchChartDetails();
    }
  }

  fetchChartDetails() {
    // fetch chart data
    this.store.dispatch(FetchWeightChartDataRequest({
      payload: {
        ...(this.memberId ? {userId: this.memberId} : {}),
        ...(this.groupId ? {groupId: this.groupId} : {}),
        from: this.fromDate ? moment(this.fromDate).format('YYYY-MM-DD') : this.fromDate,
        to: this.toDate ? moment(this.toDate).format('YYYY-MM-DD') : this.toDate,
        isCoachEntered: this.isCoachEntered,
      },
      forPrint: !!this.printData,
    }))

    // fetch chart table data
    if (this.printData) {
      this.fetchTableData(1, -1);
    } else {
      this.fetchTableData();
    }
  }

  private fetchTableData(pageNumber = 1, limit = 10) {
    this.store.dispatch(FetchWeightChartTableDataRequest({
      payload: {
        ...(this.memberId ? {userId: this.memberId} : {}),
        ...(this.groupId ? {groupId: this.groupId} : {}),
        from: this.fromDate ? moment(this.fromDate).format('YYYY-MM-DD') : this.fromDate,
        to: this.toDate ? moment(this.toDate).format('YYYY-MM-DD') : this.toDate,
        isCoachEntered: this.isCoachEntered,
        pageNumber,
        limit,
        sortColumn: this.sortColumn,
        sortOrder: this.sortOrder,
      },
      forPrint: !!this.printData,
    }))
  }

  onSort(column: string) {
    if (this.sortColumn === column) {
      // Toggle sort order
      this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      // New column, default to asc
      this.sortColumn = column;
      this.sortOrder = 'asc';
    }
    // Fetch data with new sorting
    if (this.printData) {
      this.fetchTableData(1, -1);
    } else {
      this.fetchTableData();
    }
  }

  onScroll() {
    if (!this.chartTableData?.loading && this.chartTableData?.data?.hasNext) {
      this.fetchTableData(this.chartTableData?.data?.pageNumber + 1, this.chartTableData?.data?.limit);
    }
  }

  onRoleToggle(item: any) {
    item.checked = !item.checked;
    // this.lineChartComponent.toggleLine(item.lineId);

    // check roles and fire fetch
    const selectedRoles = this.legendList.filter((item) => item.checked);
    if (selectedRoles.length === 2) {
      this.isCoachEntered = null;
      this.fetchChartDetails();
    } else if (selectedRoles.length === 1) {
      this.isCoachEntered = selectedRoles[0].isCoachEntered;
      this.fetchChartDetails();
    } else {
      this.chartData = null;
      this.chartTableData = null;
    }
  }

  checkDataReadyForPrint() {
    if (this.chartData?.data && this.chartTableData?.data?.list?.length) {
      if (this.initialLoad) {
        this.initialLoad = false;
        return
      }
      this.printService.start(this.printData?.title);
    }
  }

  ngOnDestroy() {
    this.onDestroy.next();
    this.onDestroy.complete();
  }

  protected readonly length = length;
}
